import React from "react";
import { Button } from "@whatslly/ui-components/v2/Button";
import styles from "./auto-sync-settings-page.module.scss";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { IModalInstance, modalService } from "@whatslly/ui-components/modals";
import { observer } from "mobx-react-lite";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { useDependencyInjector } from "@whatslly/commons/di";
import { AutoSyncSettingsStore } from "../../stores/AutoSyncSettingsStore";
import { useTranslation } from "react-i18next";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { LOADER_IMG } from "@whatslly/commons/constants";
import { CrmComponentsErrorBox } from "@whatslly/ui-components/v2/CrmComponentsErrorBox";

const DeleteMessagesModal: React.FC<IModalInstance<boolean>> = observer(({ closeModal }) => {
  const autoSyncSettingsStore = useDependencyInjector(AutoSyncSettingsStore);
  const { t } = useTranslation();

  return (
    <ConfirmModal
      isSubmitDisabled={autoSyncSettingsStore.isSubmitDisabled}
      closeModal={(res) => closeModal(res?.result)}
      okText={t("autoSyncPage.modalButton")}
      title={t("autoSyncPage.modalTitle")}
    >
      <div className={styles.confirmDeleteModal}>
        <div className={styles.confirmDeleteModalText}>
          <div>{t("autoSyncPage.confirmDeleteModalText")}</div>
          <br />
          <div>{t("autoSyncPage.confirmDeleteModalConfirm")}</div>
        </div>
        <TextInput
          data-testid={`delete-all-input-confirm`}
          paddingInline="none"
          placeholder={t("autoSyncPage.delete_all_placeholder")}
          value={autoSyncSettingsStore.confirmText}
          onChange={(e) => autoSyncSettingsStore.setConfirmText(e.target.value)}
          onClear={() => autoSyncSettingsStore.setConfirmText("")}
        />
      </div>
    </ConfirmModal>
  );
});

export const AutoSyncSettingsPage: React.FC = observer(() => {
  const autoSyncSettingsStore = useDependencyInjector(AutoSyncSettingsStore);
  const { t } = useTranslation();

  const onClickOnDelete = async () => {
    const res = await modalService.openModal(DeleteMessagesModal).promise;
    if (!res) {
      return;
    }
    try {
      autoSyncSettingsStore.setIsDeleting(true);
      await autoSyncSettingsStore.deleteAllMessages();
      modalService.openModal(Toast, {
        text: t("autoSyncPage.delete_success"),
      });
    } catch {
      modalService.openModal(Toast, {
        text: t("autoSyncPage.delete_failure"),
      });
    } finally {
      autoSyncSettingsStore.setIsDeleting(false);
    }
  };

  return autoSyncSettingsStore.init.case({
    pending: () => {
      return (
        <div className={styles.page}>
          <div className={styles.header} data-testid={"auto-sync-header"}>
            {t("autoSyncPage.header")}
          </div>
          <div className={styles.loaderWrapper}>
            <div className={styles.loader}>
              <img src={LOADER_IMG} alt="loader" />
              <div>{t("autoSyncPage.loading")}</div>
            </div>
          </div>
        </div>
      );
    },
    rejected: (error) => {
      return (
        <div className={styles.page}>
          <div className={styles.header} data-testid={"auto-sync-header"}>
            {t("autoSyncPage.header")}
          </div>
          <CrmComponentsErrorBox error={error.message} />
        </div>
      );
    },
    fulfilled: () => {
      return (
        <div className={styles.page}>
          <div className={styles.header} data-testid={"auto-sync-header"}>
            {t("autoSyncPage.header")}
          </div>
          <div className={styles.desc}>{t("autoSyncPage.desc")}</div>
          <Button
            isSubmitting={autoSyncSettingsStore.isDeleting}
            disabled={autoSyncSettingsStore.isDeleting}
            paddingInline="small"
            fontSize="small"
            className={styles.button}
            testId={`auto-sync-delete-all`}
            onClick={onClickOnDelete}
          >
            {t("autoSyncPage.button")}
          </Button>
        </div>
      );
    },
  });
});
